import React, { Children, isValidElement, forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { duration } from './primitives/tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { Pressable, type PressEffect } from './primitives/motion';
import { Tooltip } from './Tooltip';
import styles from './Button.module.css';

// ── Button CVA definition ─────────────────────────────────────────────────

const button = cva(styles.base, {
  variants: {
    variant: {
      default: styles.default,
      primary: styles.primary,
      text: styles.text,
      link: styles.link,
    },
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
    block: { true: styles.block },
    loading: { true: styles.loading },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

// ── Types ─────────────────────────────────────────────────────────────────

type ButtonVariant = 'default' | 'primary' | 'text' | 'link';
type ButtonIntent = 'danger' | 'success' | 'warning';
type Size = 'sm' | 'md' | 'lg';

type ButtonMotionSafeProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'>;

interface ButtonProps extends ButtonMotionSafeProps {
  children?: React.ReactNode;
  /** Button size */
  size?: Size;
  /** Full-radius pill shape */
  pill?: boolean;
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Danger/destructive action styling (legacy — prefer intent="danger") */
  danger?: boolean;
  /** Semantic intent: applies pastel container colors for hover states */
  intent?: ButtonIntent;
  /** Show loading spinner and disable interaction */
  loading?: boolean;
  /** Icon element rendered before children */
  icon?: React.ReactNode;
  /** Full-width button */
  block?: boolean;
  /** HTML button type */
  htmlType?: 'button' | 'submit' | 'reset';
  /** Active / pressed state — toolbar toggle buttons. Applies `.active` modifier and `aria-pressed`. */
  active?: boolean;
  /**
   * Press feedback effect. Opt-in — defaults to `'none'`.
   * - `'glow'` — MUI-style ripple expands from the click point, clipped to the button shape
   * - `'ring'` — border ring pulses outward from the button edge on click
   * - `'none'` — no press effect (default)
   */
  pressEffect?: PressEffect;
  /** Accepted but ignored — prevents leaking to DOM when passed from parent Input wrappers */
  focusEffect?: boolean;
}

// ── Intent class map ──────────────────────────────────────────────────────

const intentClassMap: Record<ButtonIntent, string> = {
  danger: styles.intentDanger,
  success: styles.intentSuccess,
  warning: styles.intentWarning,
};

// ── Spinner sizes ─────────────────────────────────────────────────────────

const spinnerSizeClass: Record<Size, string> = {
  sm: styles.spinnerSm,
  md: styles.spinnerMd,
  lg: styles.spinnerLg,
};

// ── Button ────────────────────────────────────────────────────────────────

/**
 * Button — primary action button with multiple variants.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Height and font |
 * | pill | boolean | false | Full-radius corners |
 * | variant | 'default' \| 'primary' \| 'text' \| 'link' | 'default' | Visual style |
 * | danger | boolean | false | Red/destructive styling |
 * | loading | boolean | false | Show spinner, disable click |
 * | icon | ReactNode | — | Icon before label |
 * | block | boolean | false | Full-width |
 * | htmlType | string | 'button' | HTML button type |
 * | active | boolean | — | Pressed/active state; sets `aria-pressed` |
 *
 * @example
 * <Button variant="primary">Save</Button>
 * <Button variant="primary" danger>Delete</Button>
 * <Button loading>Saving...</Button>
 * <Button icon={<Plus size={16} />}>Add Item</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  children,
  size = 'md',
  pill = false,
  variant = 'default',
  danger = false,
  intent,
  loading = false,
  icon,
  block = false,
  htmlType = 'button',
  active,
  pressEffect = 'none',
  style,
  disabled,
  className,
  focusEffect: _focusEffect,
  ...props
}, ref) {
  const shouldReduce = useReducedMotion();
  const isDisabled = disabled || loading;

  // Determine extra class for danger boolean modifier
  const dangerClass = danger && !intent ? styles.danger : undefined;
  // Determine extra class for intent modifier
  const intentClass = intent ? intentClassMap[intent] : undefined;

  const computedClassName = clsx(
    button({ variant, size, block, loading }),
    dangerClass,
    intentClass,
    active && styles.active,
    pill && styles.pill,
    className,
  );

  return (
    <Pressable
      effect={pressEffect}
      disabled={isDisabled}
    >
      <motion.button
        ref={ref}
        type={htmlType}
        disabled={isDisabled}
        className={computedClassName}
        style={style}
        aria-pressed={active !== undefined ? active : undefined}
        whileTap={isDisabled || shouldReduce ? undefined : { scale: 0.98 }}
        transition={{ duration: duration.normal / 1000 }}
        {...props}
      >
        <AnimatePresence mode="wait" initial={false}>
          {loading ? (
            <motion.span
              key="spinner"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={shouldReduce ? { duration: 0 } : { duration: duration.normal / 1000 }}
              className={clsx(styles.spinner, spinnerSizeClass[size])}
            />
          ) : icon ? (
            <motion.span
              key="icon"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={shouldReduce ? { duration: 0 } : { duration: duration.normal / 1000 }}
            >
              {icon}
            </motion.span>
          ) : null}
        </AnimatePresence>
        {children && <span className={styles.label}>{children}</span>}
      </motion.button>
    </Pressable>
  );
});

// ── IconButton — no background, opacity-based, for icon-only actions ──────

const iconButton = cva(styles.iconBase, {
  variants: {
    size: {
      sm: styles.iconSm,
      md: styles.iconMd,
      lg: styles.iconLg,
    },
    transparent: { true: styles.iconTransparent },
    rounded: { true: styles.iconRounded },
    elevated: { true: styles.iconElevated },
  },
  defaultVariants: {
    size: 'md',
  },
});

interface IconButtonProps extends ButtonMotionSafeProps {
  children?: React.ReactNode;
  /** Button size */
  size?: Size;
  /** Full-radius pill shape */
  pill?: boolean;
  /** Custom hover background color — passed as CSS custom property */
  hoverBackground?: string;
  /** Transparent mode: full opacity, no background, only hover bg. Use for toolbars. */
  transparent?: boolean;
  /** Rounded pill shape — applies `border-radius: var(--radius-full)` with extra padding for an oval appearance */
  rounded?: boolean;
  /** Elevated style — surface bg, subtle border, soft shadow. Use for feature-page header actions. */
  elevated?: boolean;
  /** Active / pressed state — toolbar toggle buttons. Applies `.iconActive` modifier and `aria-pressed`. */
  active?: boolean;
  /**
   * Press feedback effect.
   * - `'glow'` — radial halo expands outward on click (default)
   * - `'ring'` — border ring pulses outward on click
   * - `'none'` — no press effect
   */
  pressEffect?: PressEffect;
}

/**
 * IconButton — ghost-style square button for icon-only actions.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Square dimensions |
 * | pill | boolean | false | Full-radius corners |
 * | rounded | boolean | false | Pill shape with extra horizontal padding for oval appearance |
 * | hoverBackground | string | --surface-container | Custom hover bg |
 * | elevated | boolean | false | Surface bg + subtle border + shadow — for page header actions |
 * | active | boolean | — | Pressed/active state; applies `.iconActive` and sets `aria-pressed` |
 *
 * @example
 * <IconButton aria-label="Close"><X size={16} /></IconButton>
 * <IconButton aria-label="Delete" hoverBackground="color-mix(in srgb, var(--error) 15%, transparent)">
 *   <Trash size={16} />
 * </IconButton>
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({
  children,
  size = 'md',
  pill: _pill = false,
  hoverBackground,
  transparent = false,
  rounded = false,
  elevated = false,
  active,
  pressEffect = 'none',
  style,
  title,
  className,
  ...props
}, ref) {
  const shouldReduce = useReducedMotion();

  // Infer accessible name from icon child displayName/name when not explicitly provided
  let ariaLabel = props['aria-label'] ?? (typeof title === 'string' ? title : undefined);
  if (!ariaLabel) {
    const childArray = Children.toArray(children);
    const child = childArray.length === 1 ? childArray[0] : null;
    if (child && isValidElement(child)) {
      const name = (child.type as { displayName?: string; name?: string }).displayName
        ?? (child.type as { name?: string }).name;
      if (name) {
        ariaLabel = name.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
      }
    }
  }

  // hoverBackground is a runtime value — pass via CSS custom property so the module can consume it
  const iconStyle = hoverBackground
    ? { ...style, '--icon-btn-hover-bg': hoverBackground } as React.CSSProperties
    : style;

  const btn = (
    <Pressable
      effect={pressEffect}
      disabled={props.disabled}
    >
      <motion.button
        ref={ref}
        className={clsx(iconButton({ size, transparent, rounded, elevated }), active && styles.iconActive, className)}
        style={iconStyle}
        aria-pressed={active !== undefined ? active : undefined}
        whileTap={shouldReduce ? undefined : { scale: 0.98 }}
        transition={{ duration: duration.normal / 1000 }}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </motion.button>
    </Pressable>
  );

  if (title) {
    return <Tooltip title={title} placement="bottom">{btn}</Tooltip>;
  }

  return btn;
});
